import { tr } from '@/lib/i18n-core';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import { ApiError, get, post } from '@/lib/api';
import { printDocument } from '@/lib/print';
import { labelChunks } from './label-sheet';
import { labelSettingsFrom, probePrintBridge, printLabels, type PrintBridgeHealth } from '@/lib/print-bridge';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button, Field, Input, Select } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';

export interface LabelTarget {
  id: string;
  accessionNo: string;
  patientName: string;
  mrn: string;
  testName: string;
  specimenType: string;
  containerType: string | null;
}

interface SettingsResponse {
  settings: Record<string, unknown>;
}

interface PrintedTube {
  accessionNo: string;
  mrn: string;
  patientName: string;
  specimen: string;
}

interface PrintedLabels {
  html: string;
  labels: PrintedTube[];
  reprints: string[];
}

const REPRINT_REASONS = ['Printer jam', 'Damaged label', 'Extra tube'];
const OTHER = 'other';

function detailMessages(error: unknown, path: string): string[] {
  return error instanceof ApiError ? error.details.filter((detail) => detail.path === path).map((detail) => detail.message) : [];
}

export function LabelPrintDialog({ labels, onClose }: { labels: LabelTarget[]; onClose: () => void }) {
  const toast = useToast();
  const [copies, setCopies] = useState(1);
  const [printer, setPrinter] = useState('');
  const [bridge, setBridge] = useState<PrintBridgeHealth | null | undefined>(undefined);
  const [busy, setBusy] = useState<'sheet' | 'bridge' | 'request' | null>(null);
  const [reprints, setReprints] = useState<string[]>([]);
  const [needsApproval, setNeedsApproval] = useState<string[]>([]);
  const [reasonChoice, setReasonChoice] = useState('');
  const [otherReason, setOtherReason] = useState('');

  const settings = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: () => get<SettingsResponse>('/org/settings'),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const label = labelSettingsFrom(settings.data?.settings);

  useEffect(() => {
    if (settings.isLoading) return;
    let cancelled = false;
    void probePrintBridge(label.port).then((health) => {
      if (!cancelled) setBridge(health);
    });
    return () => {
      cancelled = true;
    };
  }, [settings.isLoading, label.port]);

  const reason = reasonChoice === OTHER ? otherReason.trim() : reasonChoice;
  const reasonReady = reprints.length === 0 || reason.length >= 3;
  const itemIds = [...new Set(labels.map((target) => target.id))];
  const tubes = new Map<string, LabelTarget[]>();
  for (const target of labels) tubes.set(target.accessionNo, [...(tubes.get(target.accessionNo) ?? []), target]);

  const printAll = async (mode: 'sheet' | 'bridge') => {
    setBusy(mode);
    try {
      const printed: PrintedLabels[] = [];
      for (const chunk of labelChunks(itemIds)) {
        printed.push(await post<PrintedLabels>('/lab/labels/print', { itemIds: chunk, copies, ...(reason ? { reason } : {}) }));
      }
      if (mode === 'sheet') {
        for (const doc of printed) printDocument(doc.html);
        onClose();
        return;
      }
      const items = printed.flatMap((doc) =>
        doc.labels.flatMap((tube) => Array.from({ length: copies }, () => ({ barcode: tube.accessionNo, topText: `${tube.mrn} ${tube.specimen}`.slice(0, 28) }))),
      );
      const result = await printLabels(items, label, printer || undefined);
      if (result.ok) {
        toast.success(`${items.length} label(s) sent to ${result.printer ?? 'the label printer'}`);
        onClose();
      } else {
        toast.error(tr('lab', 'the_label_printer_did_not_take_the_job'), result.error);
      }
    } catch (caught) {
      const needReason = detailMessages(caught, 'reason');
      const needApproval = detailMessages(caught, 'approval');
      if (needReason.length > 0) {
        setReprints(needReason);
        return;
      }
      if (needApproval.length > 0) {
        setNeedsApproval(needApproval);
        return;
      }
      toast.error(tr('lab', 'the_labels_could_not_be_printed'), caught instanceof Error ? caught.message : undefined);
    } finally {
      setBusy(null);
    }
  };

  const askApproval = async () => {
    setBusy('request');
    try {
      const wanted = labels.filter((target) => needsApproval.includes(target.accessionNo)).map((target) => target.id);
      await post('/lab/labels/reprint-requests', { itemIds: [...new Set(wanted)], reason });
      toast.success(tr('lab', 'approval_requested'), tr('lab', 'print_again_once_a_supervisor_approves_the'));
      onClose();
    } catch (caught) {
      toast.error(tr('lab', 'could_not_ask_for_approval'), caught instanceof Error ? caught.message : undefined);
    } finally {
      setBusy(null);
    }
  };

  const printers = bridge?.printers ?? [];
  const bridgeUp = Boolean(bridge);
  const printerOffline = printers.length > 0 && printers.every((p) => p.offline);
  const bridgeUnusable = bridge === null || printerOffline;

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('lab', 'specimen_labels')}
      description={tr('lab', 'x_tube_s_x_x_mm_x', { size: tubes.size, widthMm: label.widthMm, heightMm: label.heightMm, language: label.language })}
      footer={
        needsApproval.length > 0 ? (
          <>
            <Button variant="ghost" onClick={onClose}>
              {tr('lab', 'cancel')}
            </Button>
            <Button onClick={() => void askApproval()} loading={busy === 'request'} disabled={reason.length < 3}>
              {tr('lab', 'ask_a_supervisor_to_approve')}
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              {tr('lab', 'cancel')}
            </Button>
            <Button variant={bridgeUnusable ? 'default' : 'outline'} onClick={() => void printAll('sheet')} loading={busy === 'sheet'} disabled={!reasonReady}>
              {tr('lab', 'print_label_sheet')}
            </Button>
            <Button onClick={() => void printAll('bridge')} disabled={!bridgeUp || printerOffline || !reasonReady} loading={busy === 'bridge'}>
              <Printer className="me-1 h-4 w-4" /> {tr('lab', 'print_on_the_label_printer')}
            </Button>
          </>
        )
      }
    >
      <div className="space-y-4">
        {bridge === undefined && <p className="text-sm text-muted-foreground">{tr('lab', 'looking_for_the_label_printer_on_this_computer')}</p>}
        {bridge === null && (
          <Alert variant="warning">
            {tr('lab', 'no_print_bridge_answered_on_this_computer_port')} {label.port}{tr('lab', 'start_the_label_print_bridge_here_or_print_the')}
          </Alert>
        )}
        {bridge && printerOffline && <Alert variant="destructive">{tr('lab', 'the_bridge_is_running_but_every_printer_it_sees')}</Alert>}
        {bridge && !printerOffline && (
          <Alert variant="success">
            {tr('lab', 'print_bridge')} {bridge.version ? `v${bridge.version} ` : ''}{tr('lab', 'ready')}{bridge.defaultPrinter ? ` · default printer ${bridge.defaultPrinter}` : ''}.
          </Alert>
        )}
        {(reprints.length > 0 || needsApproval.length > 0) && (
          <div className="space-y-2 rounded-md border border-warning/40 p-3">
            <p className="text-sm font-medium">
              {needsApproval.length > 0
                ? tr('lab', 'a_supervisor_must_approve_reprinting_x', { value: needsApproval.join(', ') })
                : tr('lab', 'already_printed_x_say_why_they_are_printed_again', { value: reprints.join(', ') })}
            </p>
            <Field label={tr('lab', 'reason')}>
              <ChoiceButtons<string>
                layout="chips"
                value={reasonChoice}
                onChange={setReasonChoice}
                options={[...REPRINT_REASONS.map((choice) => ({ value: choice, label: choice })), { value: OTHER, label: tr('lab', 'other') }]}
              />
            </Field>
            {reasonChoice === OTHER && <Input aria-label={tr('lab', 'other_reason')} maxLength={200} value={otherReason} onChange={(e) => setOtherReason(e.target.value)} />}
          </div>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={tr('lab', 'copies_per_tube')}>
            <ChoiceButtons<string>
              inline
              value={String(copies)}
              onChange={(value) => setCopies(Number(value))}
              options={[1, 2, 3].map((n) => ({ value: String(n), label: String(n) }))}
            />
          </Field>
          {printers.length > 0 && (
            <Field label={tr('lab', 'printer')}>
              <Select value={printer} onChange={(e) => setPrinter(e.target.value)}>
                <option value="">{tr('lab', 'bridge_default')}</option>
                {printers.map((p) => (
                  <option key={p.name} value={p.name}>
                    {p.name}
                    {p.offline ? ' (offline)' : ''}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </div>
        <ul className="max-h-56 space-y-1 overflow-y-auto rounded-md border p-2 text-sm">
          {[...tubes.entries()].map(([accession, targets]) => (
            <li key={accession} className="flex items-center justify-between gap-2">
              <span>
                <span className="font-mono">{accession}</span> · {targets[0].patientName}
              </span>
              <span className="text-xs text-muted-foreground">
                {targets.map((target) => target.testName).join(', ')} · {targets[0].specimenType}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </Dialog>
  );
}
